import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbModal from '../security-toolbox-ui/StbModal';
import { LabAgent } from '../../support/labService';
import { labErrorMessage } from './LabShared';

export type LabAgentRevokeModalProps = {
  agent: LabAgent | null;
  pending?: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
};

/**
 * Revoke an agent's token.
 *
 * This stops the pod executing anything and stops it reporting health, so during an event it
 * is the right move for an agent that has been tampered with and the wrong one for an agent
 * that is merely quiet. The reason is required because the row survives revocation - it is
 * the record of why that pod went dark.
 */
const LabAgentRevokeModal: React.FC<LabAgentRevokeModalProps> = ({
  agent,
  pending = false,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!agent) return;

    setReason('');
    setError(null);
  }, [agent]);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError('Say why. The agent row keeps this.');
      return;
    }

    setError(null);

    try {
      await onConfirm(reason.trim());
    } catch (exc) {
      setError(labErrorMessage(exc, 'Could not revoke the agent.'));
    }
  };

  return (
    <StbModal
      open={!!agent}
      role="delete"
      title="Revoke agent"
      submitLabel="Revoke"
      submitButtonRole="delete"
      submitDisabled={pending}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={error ? <p className="m-0 text-sm text-amber-800">{error}</p> : undefined}
    >
      <div className="flex flex-col gap-3">
        <p className="m-0 text-sm text-gray-700">
          {`${agent?.name || agent?.agent_key} stops being able to pull work or report health as soon as this is saved. `}
          {agent?.session_key
            ? `Session ${agent.session_key} will have no executor until a new agent is enrolled there.`
            : 'Nothing else takes over its work.'}
        </p>

        <p className="m-0 text-xs text-gray-500">
          The row stays. Its token does not - a replacement gets a new one, shown once.
        </p>

        <TextField
          label="Reason"
          value={reason}
          onChange={(evt) => setReason(evt.target.value)}
          multiline
          minRows={2}
          fullWidth
        />
      </div>
    </StbModal>
  );
};

export default LabAgentRevokeModal;
